import { memo } from "react";
import { Calendar, Clock, MapPin } from "lucide-react";
import type { GoldenSerenityComponentProps } from "./types";
import type { EventCeremony } from "@/services/api/eventSettings";
import { GS, formatDate } from "./utils";
import { AmbientGlow, OrganicDivider } from "./decorations";

// JUB-154: "baptism" is a separately held baptism (wedding + baptism events).
const ceremonyOrder: Array<"civil" | "religious" | "baptism" | "party"> = ["civil", "religious", "baptism", "party"];

const buildMapsUrl = (ceremony: EventCeremony): string | null => {
  const addressParts = [ceremony.address, ceremony.venue, ceremony.city].filter(Boolean);
  if (addressParts.length === 0) return null;
  return `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(addressParts.join(", "))}`;
};

export const GoldenSerenityDetails = memo(({ event, config, locale, t }: GoldenSerenityComponentProps) => {
  const ceremonies = ceremonyOrder
    .map((key) => ({ key, ceremony: event.ceremonies?.[key] }))
    .filter((entry): entry is { key: (typeof ceremonyOrder)[number]; ceremony: EventCeremony } =>
      Boolean(entry.ceremony?.enabled),
    );

  if (ceremonies.length === 0 || !(event.showTimeline ?? true)) return null;

  return (
    <section
      id="details"
      className="relative py-20 sm:py-28 overflow-hidden"
      style={{ background: `linear-gradient(to bottom, ${GS.creamLight}, ${GS.cream}, ${GS.creamDeep})` }}
    >
      <AmbientGlow />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
        {/* Section header */}
        <div className="text-center mb-12 sm:mb-16">
          <div className="mb-8">
            <OrganicDivider width={80} />
          </div>
          <h2
            className="text-4xl sm:text-6xl font-medium mb-3"
            style={{ color: config.palette.text, fontFamily: config.typography.heading }}
          >
            {t("events.form.template.eventDetails")}
          </h2>
          <p
            className="text-sm tracking-wide"
            style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
          >
            {t("events.form.template.theCelebration")}
          </p>
        </div>

        {/* Events list */}
        <div className="space-y-6">
          {ceremonies.map(({ key, ceremony }) => {
            const mapsUrl = buildMapsUrl(ceremony);
            const dayNumber = ceremony.datetime ? formatDate(ceremony.datetime, locale, { day: "numeric" }) : "";
            const monthShort = ceremony.datetime ? formatDate(ceremony.datetime, locale, { month: "short" }) : "";
            const dayLabel = ceremony.datetime
              ? formatDate(ceremony.datetime, locale, {
                  weekday: "long",
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })
              : "";
            const hasTime = Boolean(ceremony.datetime && ceremony.datetime.includes("T") && ceremony.datetime.includes(":"));
            const timeLabel = hasTime
              ? formatDate(ceremony.datetime, locale, { hour: "2-digit", minute: "2-digit" })
              : "";

            return (
              <div
                key={key}
                className="relative overflow-hidden rounded-2xl border transition-all duration-500"
                style={{
                  background: `linear-gradient(to bottom right, ${GS.creamLight}, ${GS.creamDeep})`,
                  borderColor: "rgba(232,220,200,0.4)",
                  boxShadow: "0 6px 32px rgba(139,115,85,0.12)",
                }}
              >
                <div className="relative grid grid-cols-1 md:grid-cols-12 gap-6 p-6 sm:p-8 md:p-10">
                  {/* Date block */}
                  {(monthShort || dayNumber) && (
                    <div className="md:col-span-2 flex md:flex-col items-baseline md:items-start gap-2 md:gap-0">
                      <span
                        className="text-lg uppercase tracking-wide font-semibold"
                        style={{ color: GS.gold, fontFamily: config.typography.heading }}
                      >
                        {monthShort}
                      </span>
                      <span
                        className="text-4xl md:text-5xl font-semibold leading-tight"
                        style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                      >
                        {dayNumber}
                      </span>
                    </div>
                  )}

                  {/* Event info */}
                  <div className="md:col-span-6 space-y-4">
                    {dayLabel && (
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4 shrink-0" style={{ color: GS.gold }} aria-hidden="true" />
                        <p
                          className="text-xs tracking-wide"
                          style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
                        >
                          {dayLabel}
                        </p>
                      </div>
                    )}

                    <h3
                      className="text-2xl sm:text-3xl md:text-4xl font-medium"
                      style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                    >
                      {ceremony.name}
                    </h3>

                    {timeLabel && (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4 shrink-0" style={{ color: GS.gold }} aria-hidden="true" />
                        <p
                          className="text-sm"
                          style={{ color: config.palette.text, fontFamily: config.typography.body }}
                        >
                          {timeLabel}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Location */}
                  {(ceremony.venue || ceremony.address || ceremony.city || mapsUrl) && (
                    <div className="md:col-span-4 space-y-4">
                      {(ceremony.venue || ceremony.address || ceremony.city) && (
                        <div className="flex items-start gap-2">
                          <MapPin className="w-4 h-4 shrink-0 mt-1" style={{ color: GS.gold }} aria-hidden="true" />
                          <div className="min-w-0">
                            {ceremony.venue && (
                              <p
                                className="text-lg font-medium mb-1"
                                style={{ color: config.palette.text, fontFamily: config.typography.heading }}
                              >
                                {ceremony.venue}
                              </p>
                            )}
                            {(ceremony.address || ceremony.city) && (
                              <p
                                className="text-sm leading-relaxed"
                                style={{ color: config.palette.mutedText, fontFamily: config.typography.body }}
                              >
                                {[ceremony.address, ceremony.city].filter(Boolean).join(", ")}
                              </p>
                            )}
                          </div>
                        </div>
                      )}

                      {mapsUrl && (
                        <a
                          href={mapsUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-block px-7 py-3 rounded-full text-sm font-semibold tracking-wide transition-all duration-500 hover:shadow-[0_4px_16px_rgba(201,168,106,0.25)]"
                          style={{
                            background: GS.gold,
                            // JUB-90: creamLight-on-gold was 2.1:1 (fails WCAG AA); GS.ink gives ~5.7:1
                            color: GS.ink,
                            fontFamily: config.typography.body,
                          }}
                        >
                          {t("events.form.template.viewOnMap")}
                        </a>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
});

GoldenSerenityDetails.displayName = "GoldenSerenityDetails";
